/**
 * Converts a time string and optional date string into a Date object.
 * Handles Safari's date format requirements by converting dashes to slashes.
 * 
 * @param timeString - Time in various formats ("HH:MM", "HH:MM:SS", "HH:MM A" etc.)
 * @param yearMonthDay - Optional date in format "YYYY-MM-DD" or "YYYY/MM/DD". Defaults to "1970/01/01"
 * @returns Date object representing the combined date and time
 * 
 * @example
 * // Returns Date object for "2025/02/26 14:30"
 * stringToDate("14:30", "2025-02-26")
 * 
 * @example
 * // Returns Date object for "1970/01/01 09:00" (using default date)
 * stringToDate("09:00")
 */
export function stringToDate(timeString: string, yearMonthDay: string = '1970/01/01'): Date {
    const replaceDashWithSlashes = yearMonthDay.replace(/-/g, "/");
    return new Date(`${replaceDashWithSlashes} ${timeString}`);
}

/**
 * Formats a Date object to a 12-hour time string (AM/PM format)
 * 
 * @param d - Date object to format
 * @returns Formatted time string in 12-hour format
 * 
 * @example
 * // Returns "2:30 PM"
 * formatDateTo12HourTimeString(new Date("2025/02/26 14:30"))
 * 
 * @example
 * // Returns "9:00 AM"
 * formatDateTo12HourTimeString(new Date("2025/02/26 09:00"))
 */
export function formatDateTo12HourTimeString(d: Date): string {
    return d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
}

/**
 * Formats a Date object to a 24-hour time string
 * 
 * @param d - Date object to format
 * @returns Formatted time string in 24-hour format
 * 
 * @example
 * // Returns "14:30"
 * formatDateTo24HourTimeString(new Date("2025/02/26 14:30"))
 * 
 * @example
 * // Returns "09:00"
 * formatDateTo24HourTimeString(new Date("2025/02/26 09:00"))
 */
export function formatDateTo24HourTimeString(d: Date): string {
    return d.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })
}